import * as React from "react";
import { cn } from "@/helpers/cn";

/**
 * Typography component for Headline 1
 * Helvetica Neue Regular, 94px, 102% line-height, -4% letter-spacing, capitalize
 */
export interface Headline1Props
  extends React.ComponentPropsWithoutRef<"h1"> {}

export const Headline1 = React.forwardRef<HTMLHeadingElement, Headline1Props>(
  ({ className, ...props }, ref) => {
    return (
      <h1
        ref={ref}
        className={cn(
          "font-sans text-[94px] leading-[102%] tracking-[-0.04em] capitalize font-normal",
          className
        )}
        {...props}
      />
    );
  }
);
Headline1.displayName = "Headline1";

/**
 * Typography component for Headline 2
 * Helvetica Neue Regular, 64px, 108% line-height, -3% letter-spacing, capitalize
 */
export interface Headline2Props
  extends React.ComponentPropsWithoutRef<"h2"> {}

export const Headline2 = React.forwardRef<HTMLHeadingElement, Headline2Props>(
  ({ className, ...props }, ref) => {
    return (
      <h2
        ref={ref}
        className={cn(
          "font-sans text-[64px] leading-[108%] tracking-[-0.03em] capitalize font-normal",
          className
        )}
        {...props}
      />
    );
  }
);
Headline2.displayName = "Headline2";

/**
 * Typography component for Headline 3
 * Helvetica Neue Regular, 48px, 108% line-height, -3% letter-spacing, capitalize
 */
export interface Headline3Props
  extends React.ComponentPropsWithoutRef<"h3"> {}

export const Headline3 = React.forwardRef<HTMLHeadingElement, Headline3Props>(
  ({ className, ...props }, ref) => {
    return (
      <h3
        ref={ref}
        className={cn(
          "font-sans text-[48px] leading-[108%] tracking-[-0.03em] font-normal",
          className
        )}
        {...props}
      />
    );
  }
);
Headline3.displayName = "Headline3";

/**
 * Typography component for Headline 4
 * Helvetica Neue Medium, 32px, 148% line-height, -3% letter-spacing, capitalize
 */
export interface Headline4Props
  extends React.ComponentPropsWithoutRef<"h4"> {}

export const Headline4 = React.forwardRef<HTMLHeadingElement, Headline4Props>(
  ({ className, ...props }, ref) => {
    return (
      <h4
        ref={ref}
        className={cn(
          "font-sans text-[32px] leading-[148%] tracking-[-0.03em] capitalize font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Headline4.displayName = "Headline4";

/**
 * Typography component for Headline 5
 * Helvetica Neue Medium, 24px, 164% line-height, -2% letter-spacing, capitalize
 */
export interface Headline5Props
  extends React.ComponentPropsWithoutRef<"h5"> {}

export const Headline5 = React.forwardRef<HTMLHeadingElement, Headline5Props>(
  ({ className, ...props }, ref) => {
    return (
      <h5
        ref={ref}
        className={cn(
          "font-sans text-[24px] leading-[164%] tracking-[-0.02em] capitalize font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Headline5.displayName = "Headline5";

/**
 * Typography component for Headline 6
 * IBM Plex Mono Medium, 20px, 148% line-height, -2% letter-spacing, uppercase
 */
export interface Headline6Props
  extends React.ComponentPropsWithoutRef<"h6"> {}

export const Headline6 = React.forwardRef<HTMLHeadingElement, Headline6Props>(
  ({ className, ...props }, ref) => {
    return (
      <h6
        ref={ref}
        className={cn(
          "font-mono text-[20px] leading-[148%] tracking-[-0.02em] uppercase font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Headline6.displayName = "Headline6";

/**
 * Typography component for Subtitle 1
 * IBM Plex Mono Medium, 18px, 148% line-height, 0 letter-spacing, sentence case
 */
export interface Subtitle1Props extends React.ComponentPropsWithoutRef<"p"> {}

export const Subtitle1 = React.forwardRef<
  HTMLParagraphElement,
  Subtitle1Props
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        "font-mono text-[18px] leading-[148%] tracking-[0] font-medium",
        className
      )}
      {...props}
    />
  );
});
Subtitle1.displayName = "Subtitle1";

/**
 * Typography component for Subtitle 1 Alternative
 * IBM Plex Mono Medium, 16px, 148% line-height, 0 letter-spacing, sentence case
 */
export interface Subtitle1AltProps
  extends React.ComponentPropsWithoutRef<"p"> {}

export const Subtitle1Alt = React.forwardRef<
  HTMLParagraphElement,
  Subtitle1AltProps
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        "font-mono text-[16px] leading-[148%] tracking-[0] font-medium",
        className
      )}
      {...props}
    />
  );
});
Subtitle1Alt.displayName = "Subtitle1Alt";

/**
 * Typography component for Subtitle 2
 * IBM Plex Mono Medium, 14px, 148% line-height, 0 letter-spacing, sentence case
 */
export interface Subtitle2Props extends React.ComponentPropsWithoutRef<"p"> {}

export const Subtitle2 = React.forwardRef<
  HTMLParagraphElement,
  Subtitle2Props
>(({ className, ...props }, ref) => {
  return (
    <p
      ref={ref}
      className={cn(
        "font-mono text-[14px] leading-[148%] tracking-[0] font-medium",
        className
      )}
      {...props}
    />
  );
});
Subtitle2.displayName = "Subtitle2";

/**
 * Typography component for Button text
 * IBM Plex Mono Medium, 16px, 148% line-height, 0 letter-spacing, uppercase
 */
export interface ButtonTextProps
  extends React.ComponentPropsWithoutRef<"span"> {}

export const ButtonText = React.forwardRef<HTMLSpanElement, ButtonTextProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "font-mono text-[16px] leading-[148%] tracking-[0] uppercase font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
ButtonText.displayName = "ButtonText";

/**
 * Typography component for Caption
 * IBM Plex Mono Medium, 12px, 156% line-height, 0 letter-spacing, uppercase
 */
export interface CaptionProps extends React.ComponentPropsWithoutRef<"span"> {}

export const Caption = React.forwardRef<HTMLSpanElement, CaptionProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "font-mono text-[12px] leading-[156%] tracking-[0] uppercase font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Caption.displayName = "Caption";

/**
 * Typography component for Overline
 * IBM Plex Mono Medium, 12px, 108% line-height, 15% letter-spacing, uppercase
 */
export interface OverlineProps
  extends React.ComponentPropsWithoutRef<"span"> {}

export const Overline = React.forwardRef<HTMLSpanElement, OverlineProps>(
  ({ className, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={cn(
          "font-mono text-[12px] leading-[108%] tracking-[0.15em] uppercase font-medium",
          className
        )}
        {...props}
      />
    );
  }
);
Overline.displayName = "Overline";
